import { useSoulMapLearningPath } from '@/hooks/queries/learning-paths';
import useGetPractices from '@/hooks/queries/practices/useGetPractices';
import {
  myVariablesKeys,
  useMyVariables,
} from '@/hooks/queries/variables/variables-v2';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useInitializePractice } from '@/hooks/useInitializePractice';
import { formatAuth0CallbackError, useSsoLogin } from '@/hooks/useSsoLogin';
import { useAuthStore } from '@/store/use-auth-store';
import { useLoaderStore } from '@/store/use-loader-store';
import { useUserStore } from '@/store/use-user-store';
import { useChatStore } from '@/store/useChatStore';
import { StandardResponse } from '@/types/agent';
import { resetAuthSessionFlags } from '@/utils/auth-session';
import { AUTH0_CONNECTIONS } from '@/utils/auth0-connections';
import {
  isSoulMapAuthenticatedSession,
  markSoulMapLoginFlow,
} from '@/utils/soulmap-auth';
import {
  getSoulMapLearningPath,
  getSoulMapStepPrompt,
} from '@/utils/soulmap-learning-path';
import {
  isSoulMapProfileConfigured,
  parseMyVariablesList,
  resolveSoulMapProfileForm,
  saveSoulMapProfile,
  SoulMapProfileForm,
} from '@/utils/soulmap-profile';
import { clearSoulMapProfileDraft } from '@/utils/soulmap-profile-draft';
import {
  resolveSoulMapSessionProgress,
  SoulMapLandingSessionState,
  SoulMapPracticeSummary,
} from '@/utils/soulmap-session-progress';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SoulMapAccount } from './soul-map-account';
import { SoulMapChatSession } from './soul-map-chat-session';
import './soul-map-landing.css';
import {
  ConstellationPath,
  GoogleIcon,
  PrimaryButton,
  SecondaryButton,
  ShieldIcon,
  SoulMapTopBar,
} from './soul-map-primitives';
import { SoulMapProfileFormView } from './soul-map-profile-form';

const SOULMAP_SESSION_PRACTICE_KEY = 'soulmap-session-practice-id';
const SOULMAP_SESSION_ACTION_KEY = 'soulmap-session-action-title';

type LandingScreen =
  | 'landing'
  | 'commitment'
  | 'confirmation'
  | 'register'
  | 'session';

const PILLARS = [
  {
    name: 'The Journey',
    desc: 'A guided, ~2-hour reflective odyssey through your life story — the code you were born with, your circumstances, choices, and context.',
    quote: 'I have never been listened to like this.',
  },
  {
    name: 'The Soul Map',
    desc: 'A keepsake portrait: your purpose, your principles, your growth edges — the truest reflection of who you are.',
    quote: 'The truest portrait of me that exists.',
  },
  {
    name: 'The Soul Guide',
    desc: 'An ongoing companion who knows your whole story, ready for the moments that matter most.',
    quote:
      'A wise counsel who knows me — at 11pm, before the hardest conversation of my year.',
  },
];

function LandingScreenView({ onDiscover }: { onDiscover: () => void }) {
  return (
    <div className="sm-fade-up">
      <section className="relative mx-auto max-w-4xl px-6 pb-24 pt-16 text-center md:px-12">
        <p className="sm-eyebrow mb-6">An Unparalleled Reflection Journey</p>
        <h1
          className="sm-serif"
          style={{
            fontSize: 'clamp(32px, 5.5vw, 56px)',
            lineHeight: 1.12,
            fontWeight: 500,
            color: 'var(--sm-ink)',
          }}
        >
          Discover the{' '}
          <span style={{ color: 'var(--sm-coral)' }}>truest portrait</span>
          <br />
          of you that exists.
        </h1>
        <p
          className="mx-auto mb-10 mt-6"
          style={{
            maxWidth: 480,
            fontSize: 17,
            color: 'var(--sm-muted)',
            lineHeight: 1.6,
          }}
        >
          A guided reflection journey into your purpose, your spark, and the
          story only you could tell.
        </p>
        <PrimaryButton onClick={onDiscover}>
          Discover Your Soul Map
        </PrimaryButton>
      </section>

      <section className="mx-auto max-w-2xl px-6 py-16 md:px-12">
        <div className="sm-divider" />
      </section>

      <section
        className="px-6 py-20 md:px-12"
        style={{ background: 'var(--sm-panel)' }}
      >
        <div className="mx-auto max-w-5xl">
          <h2
            className="sm-serif mb-16 text-center"
            style={{ fontSize: 'clamp(24px, 3.5vw, 34px)', fontWeight: 500 }}
          >
            What awaits you
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {PILLARS.map((pillar, i) => (
              <div
                key={pillar.name}
                className="rounded-2xl p-7"
                style={{
                  background: 'var(--sm-panel-alt)',
                  border: '1px solid var(--sm-line)',
                }}
              >
                <p className="sm-eyebrow mb-3">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3
                  className="sm-serif mb-3"
                  style={{ fontSize: 21, fontWeight: 500 }}
                >
                  {pillar.name}
                </h3>
                <p
                  style={{
                    fontSize: 14.5,
                    color: 'var(--sm-muted)',
                    lineHeight: 1.65,
                    marginBottom: 18,
                  }}
                >
                  {pillar.desc}
                </p>
                <span className="sm-quote-mark">&ldquo;</span>
                <p
                  className="sm-serif-italic -mt-2"
                  style={{ fontSize: 15, color: 'var(--sm-ink)' }}
                >
                  {pillar.quote}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-16 flex justify-center">
            <PrimaryButton onClick={onDiscover}>
              Discover Your Soul Map
            </PrimaryButton>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-20 text-center md:px-12">
        <p
          style={{ fontSize: 15.5, color: 'var(--sm-muted)', lineHeight: 1.75 }}
        >
          Your Soul Map is woven from your life story, Mentora&apos;s 5 Core
          Energies — validated over 18 years of research at Columbia Business
          School — and an energetic blueprint drawn from the moment and place of
          your birth, rooted in Vedic and Western traditions. Quiet, never
          hidden — ask, and you&apos;ll always get a straight answer.
        </p>
        <div className="mb-2 mt-8 flex items-center justify-center gap-2">
          <ShieldIcon size={15} />
          <p className="sm-eyebrow">Sanctuary-grade privacy</p>
        </div>
        <p
          style={{
            fontSize: 14,
            color: 'var(--sm-muted)',
            lineHeight: 1.65,
            maxWidth: 440,
            margin: '0 auto',
          }}
        >
          What you share stays yours — never sold, never used to train outside
          models, and never seen by anyone without your explicit permission.
          Export or erase your story at any time.
        </p>
        <p className="sm-eyebrow mt-10">
          Trusted by 100+ executives, education leaders, and coaches
        </p>
      </section>

      <section
        className="px-6 py-24 text-center md:px-12"
        style={{ background: 'var(--sm-panel)' }}
      >
        <h2
          className="sm-serif mb-8"
          style={{ fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 500 }}
        >
          Beyond your type.{' '}
          <span style={{ color: 'var(--sm-coral)' }}>Your soul, mapped.</span>
        </h2>
        <PrimaryButton onClick={onDiscover}>
          Discover Your Soul Map
        </PrimaryButton>
      </section>

      <footer className="px-6 py-8 text-center md:px-12">
        <p style={{ fontSize: 12.5, color: 'var(--sm-muted-2)' }}>
          Mentora Institute
        </p>
      </footer>
    </div>
  );
}

function CommitmentScreen({
  onBack,
  onReady,
  onGoogleAuthStart,
  onGoogleAuthCancel,
  onLogout,
  isLoggingOut,
}: {
  onBack: () => void;
  onReady: () => void;
  onGoogleAuthStart: () => void;
  onGoogleAuthCancel: () => void;
  /** The page's logout, so the account menu also knows a logout is running. */
  onLogout: () => void;
  isLoggingOut: boolean;
}) {
  const { loginWithSocialConnection, isLoading: isSsoLoading } = useSsoLogin();
  const { isLoading: isGlobalLoading } = useLoaderStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authMethod = useAuthStore((state) => state.authMethod);
  const hasSoulMapSession = isSoulMapAuthenticatedSession({
    isAuthenticated,
    authMethod,
  });
  const isConnecting = isSsoLoading || isGlobalLoading;

  const handleGoogleClick = () => {
    if (hasSoulMapSession) {
      onReady();
      return;
    }

    onGoogleAuthStart();
    // A new sign-in may be a different person on the same tab.
    clearSoulMapProfileDraft();
    markSoulMapLoginFlow();
    void loginWithSocialConnection(AUTH0_CONNECTIONS.google, {
      envOrganizationOnly: true,
    }).catch(() => {
      onGoogleAuthCancel();
    });
  };

  return (
    <div className="sm-fade-up mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center px-6 py-8 text-center">
      <p className="sm-eyebrow mb-6">Before you begin</p>
      <h1
        className="sm-serif mb-6"
        style={{
          fontSize: 'clamp(26px, 4.5vw, 38px)',
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        This isn&apos;t a five-minute quiz.
      </h1>
      <p
        style={{
          fontSize: 16,
          color: 'var(--sm-muted)',
          lineHeight: 1.7,
          maxWidth: 460,
        }}
      >
        Soul Map takes about two hours in total, across up to three sessions.
        Come back anytime to continue — we recommend keeping each session to
        around 40 minutes, to stay in the flow.
      </p>
      <p
        className="sm-serif-italic"
        style={{
          fontSize: 17,
          color: 'var(--sm-coral)',
          lineHeight: 1.6,
          maxWidth: 420,
          marginTop: 20,
        }}
      >
        Speaking vulnerably is the path to an authentic map. The more honestly
        you show up, the truer your reflection will be.
      </p>
      <p
        style={{
          fontSize: 14,
          color: 'var(--sm-muted)',
          lineHeight: 1.6,
          maxWidth: 420,
          marginTop: 14,
        }}
      >
        Users who speak their reflections aloud tend to arrive at far more
        honest, vulnerable introspection than those who type — voice is welcome
        here, whenever you&apos;re ready to use it.
      </p>

      <div className="my-10 w-full">
        <ConstellationPath activeIndex={-1} />
      </div>

      <div className="flex w-full max-w-sm flex-col gap-4">
        <div
          className={
            hasSoulMapSession ? 'sm-auth-actions mt-2' : 'flex w-full flex-col'
          }
        >
          <button
            type="button"
            className={`sm-btn-google ${hasSoulMapSession ? '' : 'mt-2'}`}
            onClick={handleGoogleClick}
            disabled={isConnecting || isLoggingOut}
          >
            {isConnecting ? (
              <>
                <svg
                  className="sm-spin"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="var(--sm-muted-2)"
                    strokeWidth="2.5"
                    opacity="0.3"
                  />
                  <path
                    d="M21 12a9 9 0 00-9-9"
                    stroke="var(--sm-periwinkle)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
                Connecting…
              </>
            ) : (
              <>
                <GoogleIcon />
                Continue with Google
              </>
            )}
          </button>
          {hasSoulMapSession ? (
            <button
              type="button"
              className="sm-link shrink-0 whitespace-nowrap"
              onClick={onLogout}
              disabled={isConnecting || isLoggingOut}
            >
              {isLoggingOut ? 'Logging out…' : 'Logout'}
            </button>
          ) : null}
        </div>

        <div className="flex items-center justify-center gap-2">
          <ShieldIcon size={12} color="var(--sm-muted-2)" />
          <span style={{ fontSize: 12, color: 'var(--sm-muted-2)' }}>
            Encrypted and private. Yours to export or erase, always.
          </span>
        </div>
        <button type="button" className="sm-link self-center" onClick={onBack}>
          Not yet — take me back
        </button>
      </div>
    </div>
  );
}

interface ConfirmationScreenProps {
  sessionState: SoulMapLandingSessionState;
  /** False until practices and the learning path have loaded at least once. */
  isSessionStateResolved: boolean;
  isLoadingRecentSession: boolean;
  onContinue: () => void;
  onResumeSession1: () => void;
  onViewSession1: () => void;
  onViewSession2: () => void;
  onBeginSession2: () => void;
  onResumeSession2: () => void;
}

function getConfirmationCopy(sessionState: SoulMapLandingSessionState): {
  title: string;
  description: string;
} {
  switch (sessionState) {
    case 'sessions_complete':
      return {
        title: 'Your Soul Map journey is complete.',
        description:
          'Soul Map and Soul Guide are both complete. Revisit either conversation anytime to reflect on your story.',
      };
    case 'session_1_complete':
      return {
        title: 'Your Soul Map is complete.',
        description:
          'You\u2019ve finished the first part of your Soul Map journey. When you\u2019re ready, begin Soul Guide — or revisit your Soul Map conversation anytime.',
      };
    case 'session_2_in_progress':
      return {
        title: 'Continue Soul Guide.',
        description:
          'You still have ground to cover in Soul Guide. Resume whenever you\u2019re ready, or revisit your completed Soul Map conversation.',
      };
    case 'session_1_in_progress':
      return {
        title: 'Pick up where you left off.',
        description:
          'Resume your Soul Map whenever you\u2019re ready and continue exactly where you stopped.',
      };
    case 'new':
    default:
      return {
        title: 'Your Soul Map awaits.',
        description:
          'Begin whenever you\u2019re ready. You can pause and pick up exactly where you left off.',
      };
  }
}

function ConfirmationScreen({
  sessionState,
  isSessionStateResolved,
  isLoadingRecentSession,
  onContinue,
  onResumeSession1,
  onViewSession1,
  onViewSession2,
  onBeginSession2,
  onResumeSession2,
}: ConfirmationScreenProps) {
  const copy = getConfirmationCopy(sessionState);

  // Until progress is known, the state reads as 'new' ("Your Soul Map
  // awaits."), which then flips to the real copy. Hold placeholders instead,
  // inside the same tree so the fade-in and constellation don't replay.
  return (
    <div
      className="sm-fade-up mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center px-6 py-8 text-center"
      aria-busy={!isSessionStateResolved}
    >
      <p className="sm-eyebrow mb-6">You&apos;re in</p>
      {isSessionStateResolved ? (
        <>
          <h1
            className="sm-serif mb-6"
            style={{
              fontSize: 'clamp(26px, 4.5vw, 38px)',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {copy.title}
          </h1>
          <p
            style={{
              fontSize: 16,
              color: 'var(--sm-muted)',
              lineHeight: 1.7,
              maxWidth: 420,
            }}
          >
            {copy.description}
          </p>
        </>
      ) : (
        <>
          <span className="sr-only">Loading your progress…</span>
          <div
            className="mb-6 flex w-full flex-col items-center"
            aria-hidden="true"
          >
            <div className="sm-skeleton h-10 w-3/4" />
          </div>
          <div
            className="flex w-full max-w-[420px] flex-col items-center gap-3"
            aria-hidden="true"
          >
            <div className="sm-skeleton h-4 w-full" />
            <div className="sm-skeleton h-4 w-5/6" />
          </div>
        </>
      )}

      <div className="my-12 w-full">
        <ConstellationPath activeIndex={-1} />
      </div>

      <div className="flex w-full max-w-sm flex-col items-center gap-3">
        {!isSessionStateResolved ? (
          <div
            className="sm-skeleton h-[52px] w-full rounded-full"
            aria-hidden="true"
          />
        ) : null}

        {isSessionStateResolved && sessionState === 'sessions_complete' ? (
          <div className="sm-confirmation-actions-row">
            <SecondaryButton
              onClick={onViewSession1}
              disabled={isLoadingRecentSession}
            >
              View Soul Map
            </SecondaryButton>
            <SecondaryButton
              onClick={onViewSession2}
              disabled={isLoadingRecentSession}
            >
              View Soul Guide
            </SecondaryButton>
          </div>
        ) : null}

        {isSessionStateResolved && sessionState === 'session_1_complete' ? (
          <>
            <PrimaryButton
              onClick={onBeginSession2}
              disabled={isLoadingRecentSession}
            >
              {isLoadingRecentSession ? 'Loading…' : 'Begin Soul Guide'}
            </PrimaryButton>
            <SecondaryButton
              onClick={onViewSession1}
              disabled={isLoadingRecentSession}
            >
              View Soul Map
            </SecondaryButton>
          </>
        ) : null}

        {isSessionStateResolved && sessionState === 'session_2_in_progress' ? (
          <div className="sm-confirmation-actions-row">
            <PrimaryButton
              onClick={onResumeSession2}
              disabled={isLoadingRecentSession}
            >
              {isLoadingRecentSession ? 'Loading…' : 'Resume Soul Guide'}
            </PrimaryButton>
            <SecondaryButton
              onClick={onViewSession1}
              disabled={isLoadingRecentSession}
            >
              View Soul Map
            </SecondaryButton>
          </div>
        ) : null}

        {isSessionStateResolved && sessionState === 'session_1_in_progress' ? (
          <PrimaryButton
            onClick={onResumeSession1}
            disabled={isLoadingRecentSession}
          >
            {isLoadingRecentSession ? 'Loading…' : 'Resume Soul Map'}
          </PrimaryButton>
        ) : null}

        {isSessionStateResolved && sessionState === 'new' ? (
          <PrimaryButton onClick={onContinue}>Continue</PrimaryButton>
        ) : null}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        <ShieldIcon size={12} color="var(--sm-muted-2)" />
        <span style={{ fontSize: 12, color: 'var(--sm-muted-2)' }}>
          Private, secure, and held with care — this is a sanctuary for your
          story.
        </span>
      </div>
    </div>
  );
}

export default function SoulMapLandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authMethod = useAuthStore((state) => state.authMethod);
  const isEstablishingSession = useLoaderStore((state) => state.isLoading);
  const hasSoulMapSession = isSoulMapAuthenticatedSession({
    isAuthenticated,
    authMethod,
  });
  const [screen, setScreen] = useState<LandingScreen>('landing');
  const { createPractice } = useInitializePractice(undefined, {
    promptsEnabled: false,
  });
  const {
    prompt: soulMapPrompt,
    learningPath,
    isLoading: isLoadingSoulMapPath,
    isFetching: isFetchingSoulMapPath,
    status: soulMapPathStatus,
    refetch: refetchSoulMapLearningPath,
  } = useSoulMapLearningPath({
    enabled:
      hasSoulMapSession &&
      !isEstablishingSession &&
      (screen === 'confirmation' ||
        screen === 'register' ||
        screen === 'session'),
    stepIndex: 0,
  });
  const session1Prompt = soulMapPrompt;
  const session2Prompt = useMemo(
    () => getSoulMapStepPrompt(learningPath, 1),
    [learningPath]
  );
  const clearMessages = useChatStore((state) => state.clearMessages);
  const queryClient = useQueryClient();
  const { logout } = useAuthQuery();
  const userId = useUserStore((state) => state.user?.id);
  const {
    data: myVariablesData,
    isLoading: isLoadingMyVariables,
    isFetching: isFetchingMyVariables,
  } = useMyVariables(undefined, {
    enabled:
      hasSoulMapSession && !isEstablishingSession && screen === 'register',
  });
  const myVariables = useMemo(
    () => parseMyVariablesList(myVariablesData),
    [myVariablesData]
  );
  const profileForm = useMemo(
    () => resolveSoulMapProfileForm(myVariables),
    [myVariables]
  );
  const isLoadingProfile =
    screen === 'register' &&
    hasSoulMapSession &&
    (isLoadingMyVariables || isFetchingMyVariables);
  const {
    data: recentPracticesData,
    isLoading: isLoadingRecentPractices,
    isFetching: isFetchingRecentPractices,
    status: recentPracticesStatus,
    refetch: refetchRecentPractices,
  } = useGetPractices(
    { has_posts: true, user_id: userId },
    hasSoulMapSession &&
      !isEstablishingSession &&
      Boolean(userId) &&
      (screen === 'confirmation' ||
        screen === 'register' ||
        screen === 'session')
  );

  const sessionProgress = useMemo(
    () =>
      resolveSoulMapSessionProgress(
        (
          recentPracticesData as
            | StandardResponse<SoulMapPracticeSummary>
            | undefined
        )?.results,
        session1Prompt,
        session2Prompt
      ),
    [recentPracticesData, session1Prompt, session2Prompt]
  );

  const isLoadingRecentSession =
    isLoadingRecentPractices ||
    isFetchingRecentPractices ||
    (hasSoulMapSession && Boolean(userId) && isLoadingSoulMapPath);

  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [actionTitle, setActionTitle] = useState<string | null>(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const pendingGoogleAuthRef = useRef(false);

  useEffect(() => {
    if (!pendingGoogleAuthRef.current || !hasSoulMapSession) return;

    pendingGoogleAuthRef.current = false;
    if (screen === 'commitment') {
      setScreen('confirmation');
    }
  }, [hasSoulMapSession, screen]);

  useEffect(() => {
    resetAuthSessionFlags();
    sessionStorage.removeItem(SOULMAP_SESSION_PRACTICE_KEY);
    sessionStorage.removeItem(SOULMAP_SESSION_ACTION_KEY);
  }, []);

  useEffect(() => {
    if (screen === 'session' && (!practiceId || !actionTitle)) {
      setScreen(hasSoulMapSession ? 'confirmation' : 'commitment');
    }
  }, [screen, practiceId, actionTitle, hasSoulMapSession]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [screen]);

  useEffect(() => {
    const authError =
      searchParams.get('error_description') || searchParams.get('error');
    if (!authError) return;

    toast.error(
      formatAuth0CallbackError(
        decodeURIComponent(authError.replace(/\+/g, ' '))
      )
    );

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('error');
    nextParams.delete('error_description');
    nextParams.delete('state');
    const qs = nextParams.toString();
    navigate(qs ? `/soulmap-login?${qs}` : '/soulmap-login', { replace: true });
  }, [navigate, searchParams]);

  useEffect(() => {
    if (
      hasSoulMapSession &&
      (screen === 'confirmation' || screen === 'register')
    ) {
      void refetchSoulMapLearningPath();
      if (userId) {
        void refetchRecentPractices();
      }
    }
  }, [
    hasSoulMapSession,
    screen,
    refetchSoulMapLearningPath,
    refetchRecentPractices,
    userId,
  ]);

  const openSession = (practice: SoulMapPracticeSummary) => {
    setPracticeId(String(practice.id));
    setActionTitle(practice.name);
    setScreen('session');
  };

  const handleResumeSession1 = () => {
    if (!sessionProgress.session1Practice) return;
    openSession(sessionProgress.session1Practice);
  };

  const handleViewSession1 = () => {
    if (!sessionProgress.session1Practice) return;
    openSession(sessionProgress.session1Practice);
  };

  const handleResumeSession2 = () => {
    if (!sessionProgress.session2Practice) return;
    openSession(sessionProgress.session2Practice);
  };

  const handleViewSession2 = () => {
    if (!sessionProgress.session2Practice) return;
    openSession(sessionProgress.session2Practice);
  };

  const handleBeginSession2 = async () => {
    let agentPrompt = session2Prompt;
    if (!agentPrompt?.name) {
      const { data } = await refetchSoulMapLearningPath();
      agentPrompt = getSoulMapStepPrompt(getSoulMapLearningPath(data), 1);
    }

    if (!agentPrompt?.name) {
      toast.error(
        'Soul Guide isn’t available just yet. Please try again in a few minutes.'
      );
      return;
    }

    setIsStartingSession(true);
    try {
      const practice = await createPractice({ name: agentPrompt.name });
      setPracticeId(String(practice.id));
      setActionTitle(agentPrompt.name);
      setScreen('session');
      void refetchRecentPractices();
    } catch (error) {
      console.error('Failed to start Soul Map Session 2:', error);
      toast.error('Could not start Soul Guide. Please try again.');
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleBeginSession = async (profile: SoulMapProfileForm) => {
    let agentPrompt = session1Prompt;
    if (!agentPrompt?.name) {
      const { data } = await refetchSoulMapLearningPath();
      agentPrompt = getSoulMapStepPrompt(getSoulMapLearningPath(data), 0);
    }

    if (!agentPrompt?.name) {
      toast.error(
        'Soul Map isn’t available just yet. Please try again in a few minutes.'
      );
      return;
    }

    setIsStartingSession(true);
    try {
      await saveSoulMapProfile(profile, myVariables);
      queryClient.invalidateQueries({ queryKey: myVariablesKeys.lists() });

      const practice = await createPractice({ name: agentPrompt.name });
      setPracticeId(String(practice.id));
      setActionTitle(agentPrompt.name);
      setScreen('session');
    } catch (error) {
      console.error('Failed to start Soul Map session:', error);
      if (!isSoulMapProfileConfigured(myVariables)) {
        toast.error(
          'We couldn’t save your details just now. Please try again, or contact support if it keeps happening.'
        );
      } else {
        toast.error('Could not start your Soul Map. Please try again.');
      }
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleRestartSession = async (title: string) => {
    if (practiceId) {
      queryClient.removeQueries({ queryKey: ['posts', Number(practiceId)] });
    }
    clearMessages();

    try {
      const practice = await createPractice({ name: title });
      setPracticeId(String(practice.id));
      setActionTitle(title);
    } catch {
      toast.error('Could not restart your conversation. Please try again.');
    }
  };

  const handleLeaveSession = () => {
    clearMessages();
    if (practiceId) {
      queryClient.removeQueries({ queryKey: ['posts', Number(practiceId)] });
    }
    setPracticeId(null);
    setActionTitle(null);
    setScreen('confirmation');
    void refetchRecentPractices();
  };

  // Leave the chat and session screens before the session is torn down: if they
  // stay mounted, clearing the user and query cache makes them refetch with no
  // credentials (401s). The sign-in step lets a different account be chosen.
  const handleLogout = () => {
    clearMessages();
    if (practiceId) {
      queryClient.removeQueries({ queryKey: ['posts', Number(practiceId)] });
    }
    setPracticeId(null);
    setActionTitle(null);
    setScreen('commitment');
    logout.mutate();
  };

  const isInSession = screen === 'session' && practiceId && actionTitle;

  // One root and top bar for every screen, so entering the chat keeps the
  // header mounted instead of rebuilding it (which made it shift briefly).
  return (
    <div
      className={`sm-root flex flex-col ${isInSession ? 'h-screen' : 'min-h-screen'}`}
      ref={containerRef}
    >
      <SoulMapTopBar
        account={
          hasSoulMapSession ? (
            <SoulMapAccount
              onLogout={handleLogout}
              isLoggingOut={logout.isPending}
              disabled={isStartingSession}
            />
          ) : null
        }
      />
      {isInSession ? (
        <SoulMapChatSession
          key={practiceId}
          practiceId={practiceId}
          actionTitle={actionTitle}
          onLeaveSession={handleLeaveSession}
          onRestartSession={handleRestartSession}
        />
      ) : null}
      {screen === 'landing' && (
        <LandingScreenView onDiscover={() => setScreen('commitment')} />
      )}
      {screen === 'commitment' && (
        <CommitmentScreen
          onBack={() => setScreen('landing')}
          onReady={() => setScreen('confirmation')}
          onGoogleAuthStart={() => {
            pendingGoogleAuthRef.current = true;
          }}
          onGoogleAuthCancel={() => {
            pendingGoogleAuthRef.current = false;
          }}
          onLogout={handleLogout}
          isLoggingOut={logout.isPending}
        />
      )}
      {screen === 'confirmation' && (
        <ConfirmationScreen
          sessionState={sessionProgress.state}
          isSessionStateResolved={
            recentPracticesStatus !== 'pending' &&
            soulMapPathStatus !== 'pending'
          }
          isLoadingRecentSession={isLoadingRecentSession || isStartingSession}
          onContinue={() => setScreen('register')}
          onResumeSession1={handleResumeSession1}
          onViewSession1={handleViewSession1}
          onViewSession2={handleViewSession2}
          onBeginSession2={handleBeginSession2}
          onResumeSession2={handleResumeSession2}
        />
      )}
      {screen === 'register' && (
        <SoulMapProfileFormView
          initialForm={profileForm}
          isLoading={isLoadingProfile}
          isSubmitting={isStartingSession}
          isLoadingAgents={isLoadingSoulMapPath || isFetchingSoulMapPath}
          agentReady={Boolean(soulMapPrompt?.name)}
          onSubmit={handleBeginSession}
        />
      )}
    </div>
  );
}
